// MACHINERY V2 (Stroje) — the v2-local media-query hook + THE BOUNDARY
// CONSTANTS (M2.16b fix pass findings F4 + F5; widened to the WHOLE module at
// the M2.16c fix pass, review finding F-A).
//
// ============================================================
//  >>> THESE CONSTANTS ARE THE MODULE'S ONLY BREAKPOINT VOCABULARY <<<
// ============================================================
// F-A: v2 used to have TWO readers of the same boundary. `shared/hooks/
// useBreakpoint` seeds from the INTEGER `window.innerWidth` and listens on the
// INTEGER queries `(max-width: 767px)` / `(max-width: 1279px)`, while both v2
// stylesheets fence their below-desktop rules at `1279.98px`. In the band
// (1279, 1279.98] — reachable with browser zoom and Windows display scaling —
// the JS resolved DESKTOP while the CSS resolved BELOW-DESKTOP, so the katalog
// rendered its desktop rail and desktop cards under mobile grid rules.
//
// Every below-desktop and phone-tier decision inside `customer/machinery/v2/`
// now goes through this hook, so the JS predicate and the CSS predicate are the
// SAME STRING and cannot disagree at any viewport, fractional or not.
//
// SEMANTICS AT INTEGER WIDTHS ARE UNCHANGED, which is why the swap was safe to
// make module-wide: at an integer `w`, `w < 1280` and `(max-width: 1279.98px)`
// agree exactly — both true at w <= 1279, both false at w >= 1280 — and the
// same holds for `w < 768` against `(max-width: 767.98px)`. The conversion
// changes behaviour ONLY inside the fractional band it exists to fix.
//
// `shared/hooks/useBreakpoint` is deliberately NOT edited: the rest of the app
// rides it, and its own missing mount-time evaluation has its own ticket. What
// changed here is that v2 no longer DEPENDS on it for a boundary decision.
//
// >>> IT EVALUATES ON MOUNT, NOT ONLY ON CHANGE. <<<
// The shared hook only ever updates from a media-query TRANSITION event, so a
// disagreement present at mount persists until the user happens to cross a
// boundary. Below, the initial state is read from `matchMedia(query).matches` —
// THE SAME PREDICATE THE CSS EVALUATES, not a width comparison — and the effect
// re-reads `.matches` unconditionally on mount, which also covers a query that
// changed between render and commit.
//
// >>> THE QUERIES BELOW ARE HALF OF A PAIR. THE OTHER HALF IS SCSS. <<<
// A JS boundary and a CSS boundary that do not coincide is exactly the F4/F-A
// defect: the class gets stamped while the rule does not match, or the rule
// matches while the JS branch has already switched. There is no build-time
// bridge from TS to SCSS in this project, so the two are kept identical BY
// STRING and by this reciprocal list — FOUR sheets, TEN fences, RE-COUNTED
// against the tree at M2.16e's fix pass:
//   · `mobile/machinery-v2-touch.scss`       — x4 (the menu-row floor, the
//     module-wide `.btn` + `button[aria-expanded]` floor, the ✕ hit area, and
//     what is left of the profile's own additions)
//   · `mobile/machinery-v2-mobile.scss`      — x4, and TWO OF THEM ARE
//     COMPOUND: the fleet grid's 768 and 1024 column steps read
//     `(min-width: N) and (max-width: 1279.98px)`. An earlier version of this
//     note counted only the bare fences, which is the exact way a maintainer
//     following "change it in both sheets" leaves two of them on the old
//     value and re-opens the fractional band this comment exists to close.
//     GREP THE NUMBER, DO NOT TRUST A COUNT — including this one.
//   · `fleet/table/machinery-v2-table.scss`  — x1
//   · `forms/formFields.scss`                — x1
// Two further mentions live in COMMENTS (machinery-v2-tokens.scss's `.btn`
// note and this file); they fence nothing but must not contradict the rest.
// Change a number here and you MUST change it in every sheet, and vice versa.
//
// WHY `.98` AND NOT `1279`: `max-width: 1279px` does not match a 1279.5px
// viewport, so an integer ceiling leaves a gap between the last width the rule
// covers and the first width the desktop layout is designed for. The fractional
// ceiling closes it, and the JS now reads that exact ceiling rather than a
// separate integer comparison — which is the F-A half of the fix.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useEffect, useState } from "react";

/** Below the desktop tier — the query both v2 stylesheets fence their
 *  below-1280 rules in, and the module's ONLY below-desktop test. */
export const V2_BELOW_DESKTOP = "(max-width: 1279.98px)";

/** The phone tier (added at the F-A conversion, same fractional convention).
 *  `MachineryV2Layout`'s 48px phone header, the SR forms' single-column grids
 *  and the wall's phone list all branch on THIS, never on an integer width.
 *  Its complement in CSS is the conventional `min-width: 768px` — the standard
 *  `.98` pairing — and the `.mv2-fleet-grid` 768/1024 steps in
 *  mobile/machinery-v2-mobile.scss are CSS-only, read by no JS branch. */
export const V2_PHONE = "(max-width: 767.98px)";

/** V3 renders a dense LIST instead of the `.ms-table` below this. Not a tier the
 *  shared hook has, and not one it may be given — see this file's header. */
export const V2_DENSE_LIST = "(max-width: 1023.98px)";

/**
 * `true` while `query` matches. One `matchMedia` listener per hook instance, so
 * call it once per VIEW and pass the boolean down rather than once per row.
 */
export const useV2MediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() =>
    typeof window === "undefined" || !window.matchMedia
      ? false
      : window.matchMedia(query).matches
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    // MOUNT-TIME EVALUATION — see the header. Not a belt-and-braces line: it is
    // the difference between this hook and the one whose omission caused F4.
    setMatches(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);

  return matches;
};

export default useV2MediaQuery;
